import { createContext, useContext, useState, useEffect, type ReactNode } from 'react'
import { useProjects } from '@/features/projects/api/useProjects'
import type { Project } from '@/types/project.types'

const STORAGE_KEY = 'qxproveit_project_id'

interface ProjectContextValue {
    selectedProject: Project | null
    setSelectedProject: (project: Project | null) => void
    projects: Project[]
    isLoading: boolean
}

const ProjectContext = createContext<ProjectContextValue | null>(null)

export function ProjectProvider({ children }: { children: ReactNode }) {
    const { data: projects = [], isLoading } = useProjects()

    const [selectedId, setSelectedId] = useState<number | null>(() => {
        const raw = localStorage.getItem(STORAGE_KEY)
        if (!raw) return null
        const n = parseInt(raw, 10)
        return isNaN(n) ? null : n
    })

    const selectedProject = projects.find((p) => p.id === selectedId) ?? null

    // Clear stale ID once projects have loaded and the stored project no longer exists
    useEffect(() => {
        if (!isLoading && selectedId !== null && !projects.find((p) => p.id === selectedId)) {
            setSelectedId(null)
            localStorage.removeItem(STORAGE_KEY)
        }
    }, [projects, isLoading, selectedId])

    const setSelectedProject = (project: Project | null) => {
        if (project) {
            setSelectedId(project.id)
            localStorage.setItem(STORAGE_KEY, String(project.id))
        } else {
            setSelectedId(null)
            localStorage.removeItem(STORAGE_KEY)
        }
    }

    return (
        <ProjectContext.Provider value={{ selectedProject, setSelectedProject, projects, isLoading }}>
            {children}
        </ProjectContext.Provider>
    )
}

export function useProjectContext(): ProjectContextValue {
    const ctx = useContext(ProjectContext)
    if (!ctx) throw new Error('useProjectContext must be used within ProjectProvider')
    return ctx
}